import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { listManualReviews, resolveManualReview } from '../../api';
import MathHtml from '../../components/shared/MathHtml';

function HtmlDiv({ html, style }) { return React.createElement(MathHtml, { tag: 'div', style, html: html || '' }); }
function HtmlSpan({ html, style }) { return React.createElement(MathHtml, { tag: 'span', style, html: html || '' }); }

const OPTS = ['A','B','C','D'];

function ManualReviewCard({ q, onResolved }) {
  const [correct, setCorrect] = useState(q.correct_option || 'A');
  const [explanation, setExplanation] = useState(q.explanation || '');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const handleResolve = async () => {
    if (!window.confirm('Mark resolved? Question will return to tier 0 (next flag goes back to Sonnet).')) return;
    setBusy(true); setMsg(null);
    try {
      const r = await resolveManualReview(q.question_id, { correct_option: correct, explanation, notes });
      setMsg({ type: 'ok', text: `Resolved. Changes: ${r.data.changes?.join('; ') || '(none)'}` });
      setTimeout(() => onResolved(q.question_id), 1500);
    } catch (e) {
      setMsg({ type: 'err', text: e.response?.data?.detail || 'Failed' });
    } finally { setBusy(false); }
  };

  return (
    <div style={{ background: 'white', border: '2px solid #EF4444', borderRadius: 12, padding: 18, marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
        <div>
          <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 15 }}>Question #{q.question_id}</div>
          <div style={{ fontSize: 12, color: '#64748B' }}>{q.subject_name} · {q.chapter_name} · {q.topic_name}</div>
        </div>
        <div style={{ background: '#FEE2E2', color: '#991B1B', padding: '4px 10px', borderRadius: 8, fontSize: 11, fontWeight: 700 }}>Flagged {q.flag_error_count}× · Tier 3</div>
      </div>
      <HtmlDiv html={q.question_text} style={{ fontSize: 15, marginBottom: 12, lineHeight: 1.7 }} />
      {q.question_image_url && <img src={q.question_image_url} alt="" style={{ maxWidth: '100%', borderRadius: 8, marginBottom: 12 }} />}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 12 }}>
        {OPTS.map(o => (
          <div key={o} style={{ padding: '8px 12px', border: '1px solid #E2E8F0', borderRadius: 6, fontSize: 13, background: q.correct_option === o ? '#D1FAE5' : 'white' }}>
            <strong>{o}.</strong> <HtmlSpan html={q[`option_${o.toLowerCase()}`] || ''} />
          </div>
        ))}
      </div>
      <div style={{ fontSize: 13, marginBottom: 6 }}>
        Current correct: <strong>{q.correct_option}</strong>
        {q.original_correct_option && <> · Original (pre-AI): <strong>{q.original_correct_option}</strong></>}
      </div>
      <details style={{ marginBottom: 12 }}>
        <summary style={{ cursor: 'pointer', fontWeight: 600, fontSize: 13, color: '#6366F1' }}>Full review audit log</summary>
        <pre style={{ whiteSpace: 'pre-wrap', background: '#F8FAFC', padding: 12, borderRadius: 8, fontSize: 12, marginTop: 8, maxHeight: 300, overflowY: 'auto' }}>{q.chatgpt_review || '(none)'}</pre>
      </details>
      <div style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 8, padding: 14 }}>
        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10 }}>Resolve manually</div>
        <div style={{ marginBottom: 10 }}>
          <label style={{ fontSize: 12, color: '#475569' }}>Correct answer:</label>
          <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
            {OPTS.map(o => (
              <button key={o} onClick={() => setCorrect(o)}
                style={{ padding: '6px 14px', borderRadius: 6, border: `2px solid ${correct === o ? '#10B981' : '#CBD5E1'}`, background: correct === o ? '#D1FAE5' : 'white', cursor: 'pointer', fontWeight: 700 }}>{o}</button>
            ))}
          </div>
        </div>
        <div style={{ marginBottom: 10 }}>
          <label style={{ fontSize: 12, color: '#475569' }}>Explanation (HTML allowed):</label>
          <textarea value={explanation} onChange={e => setExplanation(e.target.value)} rows={5}
            style={{ width: '100%', padding: 8, fontSize: 13, fontFamily: 'inherit', border: '1px solid #CBD5E1', borderRadius: 6, marginTop: 4 }} />
        </div>
        <div style={{ marginBottom: 10 }}>
          <label style={{ fontSize: 12, color: '#475569' }}>Admin notes (optional):</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
            style={{ width: '100%', padding: 8, fontSize: 13, fontFamily: 'inherit', border: '1px solid #CBD5E1', borderRadius: 6, marginTop: 4 }} />
        </div>
        <button onClick={handleResolve} disabled={busy}
          style={{ background: '#10B981', color: 'white', border: 'none', borderRadius: 8, padding: '10px 20px', fontWeight: 700, cursor: 'pointer' }}>
          {busy ? 'Resolving…' : 'Mark Resolved (Reset to Tier 0)'}
        </button>
        {msg && <span style={{ marginLeft: 12, color: msg.type === 'ok' ? '#10B981' : '#EF4444', fontSize: 13 }}>{msg.text}</span>}
      </div>
    </div>
  );
}

export default function AdminManualReviews() {
  const qc = useQueryClient();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['manual-reviews'],
    queryFn: () => listManualReviews().then(r => r.data),
  });

  if (isLoading) return <div className="spinner" />;
  if (error) return <div style={{ color: '#EF4444' }}>Error: {error.message}</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ fontFamily: 'Sora', fontWeight: 800, margin: 0 }}>Manual Reviews ({(data || []).length})</h2>
        <button onClick={refetch} className="btn btn-ghost">Refresh</button>
      </div>
      <p style={{ color: '#64748B', fontSize: 13, marginBottom: 20 }}>
        Questions flagged by users after both Claude Sonnet and Claude Opus reviews. Resolve here to reset to tier 0 — next flag starts from Sonnet again.
      </p>
      {(!data || data.length === 0) ? (
        <div style={{ background: '#F8FAFC', border: '1px dashed #CBD5E1', borderRadius: 12, padding: 40, textAlign: 'center', color: '#94A3B8' }}>
          No questions pending manual review.
        </div>
      ) : (
        data.map(q => <ManualReviewCard key={q.question_id} q={q} onResolved={() => qc.invalidateQueries({ queryKey: ['manual-reviews'] })} />)
      )}
    </div>
  );
}
